import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, Observable, Subject } from 'rxjs';

const PANEL_SABIT_ANAHTARI = 'dk-acik-sekmeler-sabit';
const KOMPAKT_ANAHTARI = 'dk-kompakt-duzen';
/** 18.10.2026: panelin yeri ve genişliği (route-tab-lazy'den taşındı; Görünüm menüsü de değiştirir). */
const PANEL_SOL_ANAHTARI = 'dk-acik-sekmeler-sol';
const PANEL_GENISLIK_ANAHTARI = 'dk-acik-sekmeler-genislik';

/** Sabit sekmeler panelinin genişlik sınırları (px). */
/** 18.10.2026 (dtokgoz): varsayılan genişlik Dar (220); Normal 260 Görünüm menüsünde seçenek olarak kalır. */
export const PANEL_GENISLIK = { varsayilan: 220, enAz: 200, enCok: 480 } as const;

function genislikSinirla(g: number): number {
  return Math.round(Math.min(PANEL_GENISLIK.enCok, Math.max(PANEL_GENISLIK.enAz, g)));
}

function genislikOku(): number {
  try {
    const v = Number(localStorage.getItem(PANEL_GENISLIK_ANAHTARI));
    return v >= PANEL_GENISLIK.enAz && v <= PANEL_GENISLIK.enCok ? v : PANEL_GENISLIK.varsayilan;
  } catch {
    return PANEL_GENISLIK.varsayilan;
  }
}

function oku(anahtar: string): boolean {
  try {
    return localStorage.getItem(anahtar) === '1';
  } catch {
    return false;
  }
}

function yaz(anahtar: string, deger: boolean) {
  try {
    localStorage.setItem(anahtar, deger ? '1' : '0');
  } catch {
    // gizli pencere vb.: yalnız bu oturumda geçerli
  }
}

/**
 * Çalışma düzeni (16.10.2026, deneme): başlıktaki "Kompakt" anahtarı, sağdaki açık sekmeler paneli
 * (dk-route-tab-lazy) ve sol menü (MainComponent.menuOpened) arasında ortak durum.
 *
 *  - Kompakt = sekmeler paneli sağa sabit + sol menü daraltılmış (ikon şeridi / açılır menü).
 *  - Anahtar açılınca ikisi birden uygulanır, kapanınca panel serbest kalır ve menü açılır.
 *  - Kullanıcı ikisinden birini elle değiştirirse (raptiye, ☰) anahtar kendiliğinden kapalı görünür.
 *
 * Sinyal değil BehaviorSubject: route-tab-lazy şablonunda sinyal okumak Angular 16.2'de kapanan sekmeleri
 * bellekte tutuyordu (bkz. 16.10.2026 sekme bellek sızıntısı commit'i).
 *
 * GERİ ALMAK İÇİN: dk-header'daki anahtar dxi-item'ı ve MainComponent'teki menuOpened set/abonelik kaldırılır;
 * panel sabitleme route-tab-lazy'de bu servis üzerinden aynen çalışmaya devam eder.
 */
@Injectable({ providedIn: 'root' })
export class CalismaDuzeniService {
  private readonly _panelSabit = new BehaviorSubject<boolean>(oku(PANEL_SABIT_ANAHTARI));
  /** Son oturumda kompakt düzen açık mıydı — aşağıdaki abonelik kaydı ezmeden önce okunur. */
  private readonly _baslangicKompakt = oku(KOMPAKT_ANAHTARI) && this._panelSabit.value;
  private readonly _menuAcik = new BehaviorSubject<boolean>(!this._baslangicKompakt);
  private readonly _menuIstegi = new Subject<boolean>();
  private readonly _panelSol = new BehaviorSubject<boolean>(oku(PANEL_SOL_ANAHTARI));
  private readonly _panelGenislik = new BehaviorSubject<number>(genislikOku());

  /** Sekmeler paneli solda mı (menünün sağında); tarayıcıda hatırlanır. */
  readonly panelSol$ = this._panelSol.pipe(distinctUntilChanged());
  /** Sabit panelin genişliği (px); tarayıcıda hatırlanır. */
  readonly panelGenislik$ = this._panelGenislik.pipe(distinctUntilChanged());
  /** Sol menü açık mı (MainComponent bildirir). */
  readonly menuAcik$ = this._menuAcik.pipe(distinctUntilChanged());

  /** Sağdaki açık sekmeler paneli sabit mi (tarayıcıda hatırlanır). */
  readonly panelSabit$ = this._panelSabit.pipe(distinctUntilChanged());
  /** MainComponent'e: menüyü aç (true) / daralt (false). */
  readonly menuIstegi$: Observable<boolean> = this._menuIstegi.asObservable();
  /** Başlıktaki anahtarın durumu. */
  readonly kompakt$ = combineLatest([this._panelSabit, this._menuAcik]).pipe(
    map(([sabit, acik]) => sabit && !acik),
    distinctUntilChanged()
  );

  constructor() {
    // Anahtarın son durumu yeniden açılışta korunsun (MainComponent ilk menü durumunu buna göre seçer)
    this.kompakt$.subscribe((k) => yaz(KOMPAKT_ANAHTARI, k));
  }

  /** Son oturumda kompakt düzen açık mıydı (sayfa yenilenince menü daraltılmış başlasın). */
  get kompaktKayitli(): boolean {
    return this._baslangicKompakt;
  }

  get panelSabit(): boolean {
    return this._panelSabit.value;
  }

  paneliSabitle(sabit: boolean) {
    yaz(PANEL_SABIT_ANAHTARI, sabit);
    this._panelSabit.next(sabit);
  }

  /** MainComponent menü her açılıp kapandığında bildirir. */
  menuDurumu(acik: boolean) {
    if (this._menuAcik.value !== acik) this._menuAcik.next(acik);
  }

  get panelSol(): boolean {
    return this._panelSol.value;
  }

  get panelGenislik(): number {
    return this._panelGenislik.value;
  }

  paneliSolaAl(sol: boolean) {
    yaz(PANEL_SOL_ANAHTARI, sol);
    this._panelSol.next(sol);
    this.boyutBildir(0);
  }

  /** Sabit panel genişliği; sınırlar içine çekilir. */
  panelGenisligi(genislik: number) {
    const g = genislikSinirla(genislik);
    try {
      localStorage.setItem(PANEL_GENISLIK_ANAHTARI, String(g));
    } catch {
      // gizli pencere vb.: yalnız bu oturumda geçerli
    }
    this._panelGenislik.next(g);
    this.boyutBildir(0);
  }

  /** Sol menüyü aç (true) / daralt (false); ☰ ile aynı etki. */
  menuyuAc(acik: boolean) {
    this._menuIstegi.next(acik);
    this.boyutBildir(450);
  }

  /** Görünüm menüsündeki "Varsayılana dön": menü açık, sekmeler üst şeritte, panel sağda ve varsayılan (Dar) genişlikte. */
  varsayilanaDon() {
    this.paneliSabitle(false);
    this.paneliSolaAl(false);
    this.panelGenisligi(PANEL_GENISLIK.varsayilan);
    this.menuyuAc(true);
  }

  /** İçerik genişliği değişince DevExtreme gridleri ve sekme şeridi yalnız pencere resize'ını dinliyor. */
  private boyutBildir(gecikme: number) {
    setTimeout(() => window.dispatchEvent(new Event('resize')), gecikme);
  }

  kompaktDegistir() {
    const kompakt = this._panelSabit.value && !this._menuAcik.value;
    this.paneliSabitle(!kompakt);
    this._menuIstegi.next(kompakt);
    // Menü çekmecesi animasyonla daralıp genişliyor; bitince gridler yeni genişliğe uysun
    setTimeout(() => window.dispatchEvent(new Event('resize')), 450);
  }
}
